import {
    getClientApplication,
    getCollaterals,
    getCollateralsAssets,
} from 'api/application'
import ClientApplication from 'api/application/interfaces/ClientApplication'
import opeConvertCollateralValues from 'containers/ApplicationSubmissionForms/OPEApplication/convertCollateralValues'
import psConvertAppValues from 'containers/ApplicationSubmissionForms/PowersportsApplication/convertAppValues'
import psConvertCollateralValues from 'containers/ApplicationSubmissionForms/PowersportsApplication/convertCollateralValues'
import rvConvertAppValues from 'containers/ApplicationSubmissionForms/RVApplication/convertAppValues'
import rvConvertCollateralValues from 'containers/ApplicationSubmissionForms/RVApplication/convertCollateralValues'
import { vehicleSelectionPowersports, vehicleSelectionRv } from 'form/fields'
import AssetType from 'state/enums/AssetType'

import { ApplicationStatusProps } from '../hooks/useReadOnlyApplication'

type ApplicationDataResult = {
    appValues: object
    app: ApplicationStatusProps
}

const getRVApplicationData = async (
    app: ClientApplication,
    abortController: AbortController,
): Promise<ApplicationDataResult> => {
    const appValues = rvConvertAppValues(app)
    const collaterals = await getCollateralsAssets(app.uuid, abortController)
    const collateralValues = rvConvertCollateralValues(collaterals)

    if (collateralValues) {
        return {
            appValues: {
                ...appValues,
                vehicle: {
                    ...vehicleSelectionRv.config.initialValue,
                    ...collateralValues,
                    primary: {
                        ...vehicleSelectionRv.config.initialValue.primary,
                        ...collateralValues.primary,
                    },
                    secondary: {
                        ...vehicleSelectionRv.config.initialValue.secondary,
                        ...(collateralValues.secondary ?? {}),
                    },
                },
            },
            app,
        }
    }

    return { appValues, app }
}

const getPowersportsApplicationData = async (
    app: ClientApplication,
    abortController: AbortController,
): Promise<ApplicationDataResult> => {
    const appValues = psConvertAppValues(app)
    const collaterals = await getCollaterals(app.uuid, abortController)
    const collateralValues = psConvertCollateralValues(collaterals)

    if (collateralValues) {
        return {
            appValues: {
                ...appValues,
                vehicle: {
                    ...vehicleSelectionPowersports.config.initialValue,
                    ...collateralValues,
                    primary: {
                        ...vehicleSelectionPowersports.config.initialValue
                            .primary,
                        ...collateralValues.primary,
                    },
                    secondary: {
                        ...vehicleSelectionPowersports.config.initialValue
                            .secondary,
                        ...(collateralValues.secondary ?? {}),
                    },
                },
            },
            app,
        }
    }

    return { appValues, app }
}

const getOPEApplicationData = async (
    app: ClientApplication,
    abortController: AbortController,
): Promise<ApplicationDataResult> => {
    const appValues = psConvertAppValues(app)
    const collaterals = await getCollateralsAssets(app.uuid, abortController)
    const collateralValues = opeConvertCollateralValues(collaterals)

    if (collateralValues) {
        return {
            appValues: {
                ...appValues,
                vehicle: {
                    ...vehicleSelectionPowersports.config.initialValue,
                    ...collateralValues,
                },
            },
            app,
        }
    }

    return { appValues, app }
}

const getApplicationData = async (
    id: string,
    abortController: AbortController,
): Promise<ApplicationDataResult> => {
    const app = await getClientApplication(id, abortController)

    if (app.asset_type === AssetType.RECREATIONAL_VEHICLE) {
        return getRVApplicationData(app, abortController)
    }

    if (app.asset_type === AssetType.OUTDOOR_POWER_EQUIPMENT) {
        return getOPEApplicationData(app, abortController)
    }

    return getPowersportsApplicationData(app, abortController)
}

export default getApplicationData
